/* External */
import React, {
  createContext, useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';
import { useSelector } from 'react-redux';

/* Ott-common */
import { MqttComponent, createMqttEventCallbacks } from '@dtvgo/mqtt';
import { gtmEvent } from '@dtvgo/gtm-event-report';

import { useGtmContext } from '../GtmContext';

/* Other */
import {
  APP_THEME,
  ENVIRONMENT,
  MQTT_FLAG,
  MQTT_REALTIME_ENTITLEMENT_FLAG,
  SERVICES_CLIENT_ID,
  SERVICES_CLIENT_ID_PREPROD,
  getAuthTokensData,
  isUserLoggedIn,
} from '../../utils';
import {
  authInterceptor,
  refreshIdToken,
} from '../../services/auth';
import { gtmMqttDimensions } from '../GtmContext/dimensions/mqtt';

const MqttContext = createContext();

async function initializeMqtt(gtmUserData) {
  const host = import.meta.env.REACT_APP_BACKEND_SERVICE_HOST;
  const { idToken } = getAuthTokensData();
  const { vrioCustomerId, iat } = jwtDecode(idToken);

  // Criar callbacks de tracking
  const eventCallbacks = createMqttEventCallbacks({
    gtmEvent,
    eventConfig: gtmMqttDimensions,
    getUserData: () => gtmUserData,
    options: {
      debounceTime: 500,
    },
  });

  const mqttComponentInstance = new MqttComponent(authInterceptor, eventCallbacks);

  try {
    const auth = await mqttComponentInstance.authMqtt(
      idToken,
      host,
      APP_THEME,
      document.location.origin.includes('preprod.directvgo') ? SERVICES_CLIENT_ID_PREPROD : SERVICES_CLIENT_ID,
      ENVIRONMENT,
    );

    const connectResult = mqttComponentInstance.connectMqtt(
      auth?.clientId,
      auth.url,
      vrioCustomerId,
      (iat * 1000),
      refreshIdToken,
      MQTT_REALTIME_ENTITLEMENT_FLAG,
      MQTT_FLAG,
    );

    const client = connectResult instanceof Promise ? (await connectResult) : connectResult;
    return client;
  } catch (error) {
    console.error('MQTT connection error:', error);
    return null;
  }
}

export function Provider({ children }) {
  const [contextValue, setContextValue] = useState('');
  const initializedRef = useRef(false);
  const isLoggedIn = isUserLoggedIn();
  const { gtmUserData } = useGtmContext();
  const selectedProfile = useSelector((state) => state.profile.selectedProfile);
  const { idToken } = getAuthTokensData();

  useEffect(() => {
    const initialize = async () => {
      if (initializedRef.current || contextValue) {
        return;
      }

      if (!MQTT_FLAG || !isUserLoggedIn() || !idToken || !gtmUserData) {
        return;
      }

      if (!gtmUserData.v_user_id || !gtmUserData.v_app_name) {
        console.warn('MQTT: Skipping initialization - gtmUserData incomplete');
        return;
      }

      initializedRef.current = true;

      // eslint-disable-next-line no-console
      console.log('MQTT: Initializing with gtmUserData:', {
        hasUserId: !!gtmUserData.v_user_id,
        hasAppName: !!gtmUserData.v_app_name,
        hasProfileId: !!gtmUserData.v_profile_id,
        keys: Object.keys(gtmUserData),
      });

      try {
        const result = await initializeMqtt(gtmUserData);
        setContextValue(result);
      } catch (error) {
        console.error('MQTT initialization error:', error);
        initializedRef.current = false;
      }
    };

    initialize();
  }, [isLoggedIn, contextValue, gtmUserData, selectedProfile, idToken]);

  return (
    <MqttContext.Provider value={contextValue}>
      {children}
    </MqttContext.Provider>
  );
}

Provider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useMqttContext() {
  return useContext(MqttContext);
}
